import api from './client';

export interface PlayerACWR {
  acuteLoad: number;
  chronicLoad: number;
  acwr: number | null;
  riskCategory: string;
  weeklySessionsCount: number;
  totalTrainingMinutes: number;
  lastTrainingDate: string | null;
}

export interface TeamLoadSummary {
  playerId: string;
  name: string;
  position: string;
  acwr: number | null;
  riskCategory: string;
  acuteLoad: number;
  chronicLoad: number;
  weeklySessionsCount: number;
  totalTrainingMinutes: number;
  calculationDate: string;
  updatedAt: string;
}

export interface LoadAlert {
  playerId: string;
  name: string;
  position: string;
  acwr: number | null;
  riskCategory: string;
  acuteLoad: number;
  chronicLoad: number;
  calculationDate: string;
  performanceTrend: 'improving' | 'declining' | 'stable' | 'insufficient_data' | null;
  avgRating7d: number | null;
  attendanceRate30d: number | null;
  avgSleepScore30d: number | null;
  avgSorenessScore30d: number | null;
}

export interface PlayerLoadTrend {
  weekStart: string;
  weeklyLoad: number;
  sessionsAttended: number;
  sessionsScheduled: number;
  avgRpe: number | null;
  totalTrainingMinutes: number;
  previousWeekLoad: number | null;
  loadChangePercent: number | null;
  avgSleepScore: number | null;
  avgSorenessScore: number | null;
  avgMoodScore: number | null;
}

interface PlayerACWRTransport {
  acute_load: number;
  chronic_load: number;
  acwr: number | null;
  risk_category: string;
  weekly_sessions_count: number;
  total_training_minutes: number;
  last_training_date: string | null;
}

interface TeamLoadSummaryTransport {
  player_id: string;
  name: string;
  position: string;
  acwr: number | null;
  risk_category: string;
  acute_load: number;
  chronic_load: number;
  weekly_sessions_count: number;
  total_training_minutes: number;
  calculation_date: string;
  updated_at: string;
}

interface LoadAlertTransport {
  player_id: string;
  name: string;
  position: string;
  acwr: number | null;
  risk_category: string;
  acute_load: number;
  chronic_load: number;
  calculation_date: string;
  performance_trend: 'improving' | 'declining' | 'stable' | 'insufficient_data' | null;
  avg_rating_7d: number | null;
  attendance_rate_30d: number | null;
  avg_sleep_score_30d: number | null;
  avg_soreness_score_30d: number | null;
}

interface PlayerLoadTrendTransport {
  week_start: string;
  weekly_load: number;
  sessions_attended: number;
  sessions_scheduled: number;
  avg_rpe: number | null;
  total_training_minutes: number;
  previous_week_load: number | null;
  load_change_percent: number | null;
  avg_sleep_score: number | null;
  avg_soreness_score: number | null;
  avg_mood_score: number | null;
}

const adaptPlayerACWR = (value: PlayerACWRTransport): PlayerACWR => ({
  acuteLoad: value.acute_load,
  chronicLoad: value.chronic_load,
  acwr: value.acwr,
  riskCategory: value.risk_category,
  weeklySessionsCount: value.weekly_sessions_count,
  totalTrainingMinutes: value.total_training_minutes,
  lastTrainingDate: value.last_training_date,
});

const adaptTeamLoadSummary = (value: TeamLoadSummaryTransport): TeamLoadSummary => ({
  playerId: value.player_id,
  name: value.name,
  position: value.position,
  acwr: value.acwr,
  riskCategory: value.risk_category,
  acuteLoad: value.acute_load,
  chronicLoad: value.chronic_load,
  weeklySessionsCount: value.weekly_sessions_count,
  totalTrainingMinutes: value.total_training_minutes,
  calculationDate: value.calculation_date,
  updatedAt: value.updated_at,
});

const adaptLoadAlert = (value: LoadAlertTransport): LoadAlert => ({
  playerId: value.player_id,
  name: value.name,
  position: value.position,
  acwr: value.acwr,
  riskCategory: value.risk_category,
  acuteLoad: value.acute_load,
  chronicLoad: value.chronic_load,
  calculationDate: value.calculation_date,
  performanceTrend: value.performance_trend,
  avgRating7d: value.avg_rating_7d,
  attendanceRate30d: value.attendance_rate_30d,
  avgSleepScore30d: value.avg_sleep_score_30d,
  avgSorenessScore30d: value.avg_soreness_score_30d,
});

const adaptPlayerLoadTrend = (value: PlayerLoadTrendTransport): PlayerLoadTrend => ({
  weekStart: value.week_start,
  weeklyLoad: value.weekly_load,
  sessionsAttended: value.sessions_attended,
  sessionsScheduled: value.sessions_scheduled,
  avgRpe: value.avg_rpe,
  totalTrainingMinutes: value.total_training_minutes,
  previousWeekLoad: value.previous_week_load,
  loadChangePercent: value.load_change_percent,
  avgSleepScore: value.avg_sleep_score,
  avgSorenessScore: value.avg_soreness_score,
  avgMoodScore: value.avg_mood_score,
});

export const loadManagementAPI = {
  getPlayerACWR: async (playerId: string): Promise<PlayerACWR> => {
    const response = await api.get<PlayerACWRTransport>(`/load-management/player/${playerId}/acwr`);
    return adaptPlayerACWR(response.data);
  },

  getTeamLoadSummary: async (): Promise<TeamLoadSummary[]> => {
    const response = await api.get<TeamLoadSummaryTransport[]>('/load-management/team-summary');
    return response.data.map(adaptTeamLoadSummary);
  },

  getLoadAlerts: async (): Promise<LoadAlert[]> => {
    const response = await api.get<LoadAlertTransport[]>('/load-management/alerts');
    return response.data.map(adaptLoadAlert);
  },

  getPlayerLoadTrends: async (playerId: string, weeks: number = 12): Promise<PlayerLoadTrend[]> => {
    const response = await api.get<PlayerLoadTrendTransport[]>(
      `/load-management/player/${playerId}/trends?weeks=${weeks}`,
    );
    return response.data.map(adaptPlayerLoadTrend);
  },
};
